import { PauseIcon, PlayIcon, StopIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useState, type ComponentType } from 'react';
import { ActionRow, type ActionRowTone } from './ActionRow';
import { ManageMonitoringStopConfirm } from './ManageMonitoringStopConfirm';
import { StartMonitoringForm } from './StartMonitoringForm';
import {
    getAvailableActions,
    getStatusDisplay,
    type MonitoringAction,
    type MonitoringActionOptions
} from './monitoringStatus';
import { StartMonitoringPatient } from '../../../services/patient';
import { StartMonitoringParams } from '../../../services/patient/delivery';

type ModalStep = 'menu' | 'stop-confirm' | 'start';

/** Pausing auto-resumes after this many days — the doctor is not asked for a date. */
const PAUSE_RESUME_DAYS = 30;
const SECONDS_PER_DAY = 24 * 60 * 60;

interface ManageMonitoringModalProps {
    isOpen: boolean;
    onClose: () => void;
    monitoringStatus?: string | null;
    /** Patient identity for the 'start' step's form (avatar, name). Not
     * needed for pause/resume/stop, which only act on `monitoringStatus`. */
    patient: StartMonitoringPatient | null;
    /** Resolves to whether the action succeeded — the modal closes on
     * success and stays open (back on the menu) on failure so the doctor can
     * retry; the caller still shows its own error toast either way. */
    onAction: (action: MonitoringAction, options?: MonitoringActionOptions) => Promise<boolean>;
    isLoading?: boolean;
}

interface ActionRowConfig {
    icon: ComponentType<{ className?: string }>;
    label: string;
    description: string;
    tone: ActionRowTone;
}

const ACTION_ROW_CONFIG: Record<MonitoringAction, ActionRowConfig> = {
    start: {
        icon: PlayIcon,
        label: 'Start monitoring',
        description: '',
        tone: 'monitor'
    },
    pause: {
        icon: PauseIcon,
        label: 'Pause monitoring',
        description: '',
        tone: 'monitor'
    },
    resume: {
        icon: PlayIcon,
        label: 'Resume monitoring',
        description: '',
        tone: 'monitor'
    },
    stop: {
        icon: StopIcon,
        label: 'Stop monitoring',
        description: '',
        tone: 'danger'
    }
};

function ManageMonitoringModal({
    isOpen,
    onClose,
    monitoringStatus,
    patient,
    onAction,
    isLoading = false
}: ManageMonitoringModalProps) {
    const [step, setStep] = useState<ModalStep>('menu');
    const [actingAction, setActingAction] = useState<MonitoringAction | null>(null);

    useEffect(() => {
        if (isOpen) {
            setStep('menu');
            setActingAction(null);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !isLoading) onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isLoading, onClose]);

    if (!isOpen) return null;

    const { label: statusLabel, pillClass } = getStatusDisplay(monitoringStatus);
    const availableActions = getAvailableActions(monitoringStatus);

    // Runs a pause/resume/stop/start call and closes the modal on success,
    // or returns to the menu on failure so the doctor can retry (the error
    // toast is shown by the caller regardless).
    const runAction = async (action: MonitoringAction, options?: MonitoringActionOptions) => {
        setActingAction(action);
        const succeeded = await onAction(action, options);
        if (succeeded) {
            onClose();
            return;
        }
        setActingAction(null);
        setStep('menu');
    };

    const handleRowClick = (action: MonitoringAction) => {
        if (isLoading) return;
        if (action === 'start') {
            setStep('start');
            return;
        }
        if (action === 'pause') {
            // One-click pause: the resume date is fixed at +30 days — the
            // doctor is deliberately not asked to pick one.
            const resumeDate = Math.floor(Date.now() / 1000) + PAUSE_RESUME_DAYS * SECONDS_PER_DAY;
            void runAction('pause', { resumeDate });
            return;
        }
        if (action === 'stop') {
            setStep('stop-confirm');
            return;
        }
        void runAction(action);
    };

    const handleStopConfirm = () => {
        void runAction('stop');
    };

    const handleStartConfirm = (params: StartMonitoringParams) => {
        void runAction('start', { quickstart: params.quickstart });
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="manage-monitoring-modal-title"
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget && !isLoading) onClose();
            }}
        >
            <div
                className="bg-white rounded-lg shadow-xl w-full max-w-[520px] p-6 relative"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-5">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <h2
                            id="manage-monitoring-modal-title"
                            className="text-xl font-bold text-gray-900 truncate"
                        >
                            Manage monitoring
                        </h2>
                        <span
                            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${pillClass}`}
                        >
                            {statusLabel}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        aria-label="Close"
                        className="text-gray-400 hover:text-gray-800 disabled:opacity-50 transition-colors duration-200 flex-shrink-0"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {step === 'menu' &&
                    (availableActions.length === 0 ? (
                        <p className="text-sm text-gray-500">
                            No actions are available for this monitoring status.
                        </p>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {availableActions.map((action) => {
                                const config = ACTION_ROW_CONFIG[action];
                                return (
                                    <ActionRow
                                        key={action}
                                        icon={config.icon}
                                        label={config.label}
                                        description={config.description}
                                        tone={config.tone}
                                        onClick={() => handleRowClick(action)}
                                        disabled={isLoading}
                                        loading={isLoading && actingAction === action}
                                    />
                                );
                            })}
                        </div>
                    ))}

                {step === 'stop-confirm' && (
                    <ManageMonitoringStopConfirm
                        onCancel={() => setStep('menu')}
                        onConfirm={handleStopConfirm}
                        isLoading={isLoading}
                    />
                )}

                {step === 'start' && (
                    <div className="-mx-6 -mb-6">
                        <StartMonitoringForm
                            patient={patient}
                            onConfirm={handleStartConfirm}
                            onCancel={() => setStep('menu')}
                            isSubmitting={isLoading && actingAction === 'start'}
                            showAlignerNumbers={false}
                        />
                    </div>
                )}

                {step === 'menu' && (
                    <div className="flex justify-end items-center gap-3 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isLoading}
                            className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50"
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export { ManageMonitoringModal };
